import { useTranslation } from 'react-i18next';
import type { MonthKey } from '../../domain/analytics';
import { formatMonthName, formatMonthYear } from '../../i18n/format';
import { BarTrend, ChartContainer, Section, useFormat } from '../../ui';

/**
 * The monthly trend on Home (an optional section of the custom Home): the
 * six months ending with the one shown, the average of the months before
 * as a guide. A bar switches Home to that month.
 */
export function HomeTrend({ trend, onSelectMonth }: { trend: { month: MonthKey; total: number | null }[]; onSelectMonth: (month: MonthKey) => void }) {
  const { t, i18n } = useTranslation();
  const { money } = useFormat();
  const whole = (minor: number) => money(minor, { whole: true });
  const shown = trend.filter((x): x is { month: MonthKey; total: number } => x.total !== null);
  if (shown.length < 2) return null;
  const before = shown.slice(0, -1);
  const average = Math.round(before.reduce((s, x) => s + x.total, 0) / before.length);
  const short = (m: MonthKey) => formatMonthName(Number(m.slice(5, 7)), i18n.language, 'short');
  const long = (m: MonthKey) => formatMonthYear(Number(m.slice(0, 4)), Number(m.slice(5, 7)), i18n.language);
  return (
    <Section title={t('home.trend')} data-testid="home-trend">
      <p className="trend-caption num">{t('analytics.average', { n: before.length, amount: whole(average) })}</p>
      <ChartContainer
        title={t('home.trendChart')}
        hideTitle
        table={{ columns: [t('analytics.month'), t('home.centerTotal')], rows: shown.map((x) => [long(x.month), whole(x.total)]) }}
      >
        <BarTrend
          label={t('home.trendChart')}
          data={shown.map((x) => ({ key: x.month, label: short(x.month), value: x.total }))}
          highlightKey={shown[shown.length - 1]!.month}
          format={whole}
          reference={{ value: average, label: t('home.average', { amount: whole(average) }) }}
          onSelect={onSelectMonth}
        />
      </ChartContainer>
    </Section>
  );
}
